import Banner from '@ben/orion/banner';
import Button from '@ben/orion/deprecated-button';
import Input from '@ben/orion/input';
import { Modal } from '@ben/orion/modals/modal';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { Collapse } from '@mantine/core';
import { isNumber, lowerCase, startCase } from 'lodash';
import { memo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  useMutationAirtableFetch,
  type AirtableFetchData,
} from '../../hooks/use-mutation-airtable-fetch';

interface AirtableHeaderProps {
  defaultValue?: string;
  onSuccess: (data: AirtableFetchData, airtableId: string) => void;
  onError: () => void;
}

const AirtableHeader = ({
  defaultValue,
  onSuccess,
  onError,
}: AirtableHeaderProps) => {
  const [airtableId, setAirtableId] = useState(defaultValue ?? '');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [successCount, setSuccessCount] = useState<number>();
  const [errorCount, setErrorCount] = useState<number>();
  const [componentImportErrors, setComponentImportErrors] = useState<string[]>(
    []
  );

  const {
    mutate: getAirtableFetch,
    isError,
    isLoading,
  } = useMutationAirtableFetch({
    onSuccess: (data) => {
      setSuccessCount(data.successfulImportsCount);
      setErrorCount(
        data.importErrors.length + data.componentImportErrors.length
      );
      setComponentImportErrors(data.componentImportErrors);
      onSuccess(data, airtableId);
      setConfirmModalOpen(false);
    },
    onError,
  });

  return (
    <Wrapper>
      <FormWrapper>
        <Input
          name="Airtable ID (optional)"
          value={airtableId}
          onChange={(event) => setAirtableId(event.target.value)}
          width="170px"
        />
        <Button
          label="Import"
          onClick={() => setConfirmModalOpen(true)}
          width="auto"
          disabled={!airtableId || isLoading}
        />
      </FormWrapper>
      <Collapse expanded={isNumber(successCount)}>
        <StyledBanner variant="information" title="Import complete">
          <Typography variant={TypographyVariant.bodyMd1} color="inherit">
            <strong>{plural(successCount, 'field')}</strong> successfully
            imported
            {!!errorCount && errorCount > 0 && (
              <>
                , <strong>{plural(errorCount, 'field')}</strong> unsuccessfully
                imported
              </>
            )}
            .
          </Typography>
          {componentImportErrors.length > 0 && (
            <Typography variant={TypographyVariant.bodyMd1} color="inherit">
              The following step components could not be imported:{' '}
              {componentImportErrors
                .map((key) => startCase(lowerCase(key)))
                .join(', ')}
              .
            </Typography>
          )}
        </StyledBanner>
      </Collapse>
      <Collapse expanded={isError}>
        <StyledBanner message="Unsuccessful Airtable import." />
      </Collapse>
      <Modal
        title="Import from Airtable?"
        isOpen={confirmModalOpen}
        onConfirm={() => {
          getAirtableFetch(airtableId);
          setConfirmModalOpen(false);
        }}
        onClose={() => setConfirmModalOpen(false)}
        secondaryText="Close"
        primaryText="Import"
      >
        <Typography variant={TypographyVariant.bodyLg1}>
          Are you sure you want to import values from Airtable? All existing
          data in mapped fields will be overridden
          <br />
          (except for windows and step components).
          <br />
          <br />
          <strong>This cannot be undone.</strong>
        </Typography>
      </Modal>
    </Wrapper>
  );
};

export default memo(AirtableHeader);

const plural = (count?: number, thing?: string) => {
  return count === 1 ? `${count} ${thing}` : `${count} ${thing}s`;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${theme.spacing(3)};
  `}
`;

const StyledBanner = styled(Banner)`
  ${({ theme }) => css`
    // Margin top to work nicely with collapse animation
    margin-top: ${theme.spacing(3)};
  `}
`;
